/**
 * Tiny document.cookie reader. Returns the value of the cookie that
 * matches one of the candidate names, or null if none match.
 *
 * The CSRF cookie name flips between dev (`senti_admin_api_csrf`) and
 * production (`__Host-senti_admin_api_csrf`) — the API client passes
 * both candidates so the same SPA bundle works in either deployment.
 *
 * Pure: takes the cookie string explicitly so it's trivially testable.
 */
export function readCookie(
  cookieString: string,
  ...candidateNames: readonly string[]
): string | null {
  if (!cookieString) return null;
  const pairs = cookieString.split(";");
  for (const raw of pairs) {
    const idx = raw.indexOf("=");
    if (idx === -1) continue;
    const name = raw.slice(0, idx).trim();
    if (candidateNames.includes(name)) {
      // Cookie values come URL-decoded by browsers; replicate that here
      // for the testable variant. Browsers don't normally emit trailing
      // whitespace inside a value, but tolerate it for the hand-rolled
      // dev fixtures we test against.
      //
      // Wrap in try/catch: a malformed `%` sequence makes
      // decodeURIComponent throw URIError, which would otherwise bubble
      // through the axios request interceptor and crash every state-
      // changing request. The current backend only emits
      // `secrets.token_urlsafe(32)` so this can't fire today, but the
      // cost of the guard is one branch and the upside is robustness.
      const value = raw.slice(idx + 1).trim();
      try {
        return decodeURIComponent(value);
      } catch {
        return null;
      }
    }
  }
  return null;
}

/** Convenience wrapper for live `document.cookie`. */
export function readBrowserCookie(
  ...candidateNames: readonly string[]
): string | null {
  if (typeof document === "undefined") return null;
  return readCookie(document.cookie, ...candidateNames);
}
